import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

/**
 * Kártyajáték az overlay KÉPERNYŐ területén.
 *
 * Fontos: ez a komponens NEM a teljes 1920x1080-at tölti ki, hanem a
 * `.screen` grid-cellát (a monitor helyét). Ezért `absolute inset-0`, és a
 * szülője a `.screen` doboz az App.jsx-ben. Így ha a stúdió-elrendezés
 * változik, a játék vele mozog, nem kell pixelt igazítani.
 *
 * A szerkezet a kapott HTML előnézetből jön, a színek viszont az overlay
 * saját palettájából (neon lila, mélylila panel). A stílus szándékosan egy scoped `<style>`
 * blokk: a 3D kártyafordítás és a glow így pontosan az marad, ami az
 * előnézetben volt, és nem keveredik az overlay többi Tailwind osztályával.
 */

export type CardConfig = {
  label: string;
  prize: string;
  prizeNote?: string;
  winnerCount: number;
};

const EspontIcon = ({ className = "" }: { className?: string }) => (
  <img src="/espont.png" alt="" className={`cg-prize-espont-icon ${className}`} />
);

/** card.prize is free-form admin text (not always "{n} ESPONT" -- could be
 * any prize), so this only swaps the standalone word, wherever it lands. */
function renderPrize(text: string) {
  return text
    .split(/(\bESPONT\b)/i)
    .map((part, i) => (/^espont$/i.test(part) ? <EspontIcon key={i} /> : part));
}

export type CardGamePhase =
  | "idle"
  | "voting"
  | "revealing"
  | "collecting"
  | "done";

export type CardGameState = {
  phase: CardGamePhase;
  cards: CardConfig[];
  percentages: number[];
  winnerCardIndex: number | null;
  /** A futó fázis teljes hossza ms-ban, a visszaszámlálóhoz. */
  phaseDurationMs: number;
  /** Mikor indult a fázis (Date.now()), a visszaszámlálóhoz. */
  phaseStartedAt: number;
  winners: string[];
  /** A szavazo kepernyo focime. Adminrol allithato, ures = DEFAULT_VOTE_TITLE. */
  voteTitle?: string;
  /**
   * Ezt a szot kell a chatbe irni a nevgyujto fazisban ahhoz, hogy valaki
   * bekeruljon a sorsolasba. Adminrol allithato, ebbol epul a ket alapertelmezett
   * felirat is. Ures = barmilyen uzenet ernyes (a regi mukodes).
   */
  collectKeyword?: string;
  /**
   * A nevgyujto kepernyo focime. Az admin panelrol allithato; ha ures, az
   * alabbi collectTitleFor() epiti a kulcsszobol. Igy egy uj kor nem kenyszeriti az
   * admint arra, hogy minden alkalommal ujra beirja ugyanazt.
   */
  collectTitle?: string;
  /** A focim alatti magyarazo sor. Szinten adminrol allithato. */
  collectSubtitle?: string;
  sponsorText?: string;
};

/**
 * A szinek az overlay sajat paletajabol jonnek, hogy a jatek ne legyen idegen
 * test a studioban: neon lila (a kepernyo-keret ragyogasa), melylila panel es
 * a megszokott masodlagos szurke szoveg.
 */
const NEON = "#8f00ff";
const NEON_DEEP = "#5b00ff";
const NEON_SOFT = "#c08cff";
const PANEL = "#200e42";
const BG = "#140a24";
/**
 * A jatek hattere 0.85 opacitasu (Pali kerese, 2026-10-11, elesben kezzel
 * finomitva 0.2 -> 0.7 -> 0.85-re, miutan latta OBS-ben): a kepernyokep
 * nagyon gyengen sejlik at mogotte. Egy szammal allithato: 1 = teljesen takar,
 * 0 = teljesen atlatszo.
 */
const BG_OPACITY = 0.85;

/** #rrggbb -> rgba(), hogy a hatter atlatszosaga egy szammal alljon. */
function hexToRgba(hex: string, alpha: number) {
  const value = Number.parseInt(hex.slice(1), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
const MUTED = "#b1b1b1";

/**
 * Alapertelmezett feliratok, ha az admin nem ad meg sajatot. Ezeknek egyezniuk
 * kell az admin panel placeholdereivel (CardGameManager).
 *
 * A nevgyujto ket sora a kulcsszobol epul: ha az admin GG-rol MERRY-re valt, a
 * kepernyo is atall, anelkul hogy barmit ujra kellene gepelni. Kulcsszo nelkul
 * (ures mezo) barmilyen uzenet ernyes, ezert megy vissza a regi, nevre kero
 * szovegre.
 */
const DEFAULT_VOTE_TITLE = "Pick one card for community";
const collectTitleFor = (keyword: string) =>
  keyword ? `Type ${keyword} in the chat!` : "Enter your username!";
const collectSubtitleFor = (keyword: string) =>
  keyword ? `Anyone who spams ${keyword} can win!` : "Spam your name in the chat";

/** Length of the closing animation, must match cg-slide-down in the CSS. */
const EXIT_MS = 800;

/** Egy kartya fordulasanak hossza, a .cg-card transition-nel egyezik. */
const FLIP_MS = 800;
/** Ennyivel kesik minden kovetkezo kartya, hogy egyesevel forduljanak. */
const FLIP_STAGGER_MS = 350;
/**
 * Ennyi ideig latszik meg a hatlap, mielott az elso kartya fordulni kezd.
 *
 * Nem kozmetika: a fazis-valtaskor a kartyak ujramountolodnak, es ha mar
 * fordítva jonnek letre, nincs mit animalni -- a lap egybol az osszeget mutatja.
 * Eloszor hatlappal renderelunk, es csak utana valtunk fordított allapotra.
 */
const PRE_FLIP_MS = 700;

/**
 * Lebego hatter-ikonok.
 *
 * A panel kozepe a kartyake, ezert minden alakzat a szelekre kerul, es
 * szandekosan alacsony opacitassal + elmosassal fut: melyseget ad, de nem
 * versenyez a jatekkal. Csak `transform` es `opacity` animalodik, tehat a
 * bongeszo compositoran fut -- egy OBS overlaynek ez nem kerul semmibe.
 *
 * A formak kartya-tematikusak (szinek, zseton, csillag, dragako), a szinezes
 * az overlay sajat lila paletajabol jon.
 */
type DecoShape =
  | "spade"
  | "heart"
  | "diamond"
  | "club"
  | "chip"
  | "star"
  | "sparkle"
  | "gem";

function DecoShapeSvg({ shape, id }: { shape: DecoShape; id: string }) {
  const fill = `url(#${id})`;
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        {/* Sajat gradiens minden alakzatnak: kulon <svg>-k kozott a megosztott
            defs torekeny, egy elemen belul viszont mindig felodik. */}
        <linearGradient id={id} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor={NEON_SOFT} />
          <stop offset="55%" stopColor={NEON} />
          <stop offset="100%" stopColor={NEON_DEEP} />
        </linearGradient>
      </defs>
      {shape === "spade" && (
        <path
          fill={fill}
          d="M50 6C50 6 16 34 16 56a18 18 0 0 0 32 11c-1 13-6 20-13 25h30c-7-5-12-12-13-25a18 18 0 0 0 32-11C84 34 50 6 50 6Z"
        />
      )}
      {shape === "heart" && (
        <path
          fill={fill}
          d="M50 90C50 90 10 63 10 38A21 21 0 0 1 50 27a21 21 0 0 1 40 11c0 25-40 52-40 52Z"
        />
      )}
      {shape === "diamond" && <path fill={fill} d="M50 4 88 50 50 96 12 50Z" />}
      {shape === "club" && (
        <path
          fill={fill}
          d="M50 8a18 18 0 0 1 15 28 18 18 0 1 1-7 32c1 11 6 18 12 23H30c6-5 11-12 12-23a18 18 0 1 1-7-32A18 18 0 0 1 50 8Z"
        />
      )}
      {shape === "chip" && (
        <>
          <path
            fill={fill}
            fillRule="evenodd"
            d="M50 2a48 48 0 1 1 0 96 48 48 0 0 1 0-96Zm0 18a30 30 0 1 0 0 60 30 30 0 0 0 0-60Z"
          />
          <g fill={fill}>
            <rect x="45" y="0" width="10" height="16" rx="3" />
            <rect x="45" y="84" width="10" height="16" rx="3" />
            <rect x="0" y="45" width="16" height="10" rx="3" />
            <rect x="84" y="45" width="16" height="10" rx="3" />
          </g>
        </>
      )}
      {shape === "star" && (
        <path
          fill={fill}
          d="M50 4 62 37h35L69 58l11 34-30-21-30 21 11-34L3 37h35Z"
        />
      )}
      {shape === "sparkle" && (
        <path
          fill={fill}
          d="M50 2c4 26 20 44 48 48-28 4-44 22-48 48-4-26-20-44-48-48C30 46 46 28 50 2Z"
        />
      )}
      {shape === "gem" && (
        <>
          <path fill={fill} d="M28 10h44l24 30-46 52L6 40Z" />
          <path fill={BG} fillOpacity=".25" d="M28 10 50 40 72 10l24 30H6Z" />
        </>
      )}
    </svg>
  );
}

type DecoItem = {
  shape: DecoShape;
  /** Szazalek a panel bal szeletol / tetejetol. */
  left: number;
  top: number;
  /** Pixel oldalhossz. */
  size: number;
  opacity: number;
  blur: number;
  /** Az oda-vissza lebeges vegallasa. */
  dx: number;
  dy: number;
  r0: number;
  r1: number;
  dur: number;
  /** Negativ, hogy az alakzatok ne egyszerre lengjenek. */
  delay: number;
};

/**
 * Kezzel elhelyezve, nem veletlenszeruen: a kozepso savot (x 24-76%) szabadon
 * kell hagyni a kartyaknak, es minden korben ugyanott kell allniuk, kulonben
 * egy fazisvaltas ujrasorsolna a hatteret.
 */
const DECO: DecoItem[] = [
  {
    shape: "spade",
    left: 3,
    top: 11,
    size: 150,
    opacity: 0.1,
    blur: 2,
    dx: 12,
    dy: -22,
    r0: -8,
    r1: 6,
    dur: 14,
    delay: -2,
  },
  {
    shape: "chip",
    left: 10,
    top: 60,
    size: 210,
    opacity: 0.07,
    blur: 5,
    dx: -16,
    dy: -26,
    r0: 4,
    r1: -10,
    dur: 18,
    delay: -7,
  },
  {
    shape: "heart",
    left: 1,
    top: 79,
    size: 96,
    opacity: 0.12,
    blur: 0,
    dx: 14,
    dy: -16,
    r0: -12,
    r1: 4,
    dur: 11,
    delay: -4,
  },
  {
    shape: "star",
    left: 17,
    top: 32,
    size: 68,
    opacity: 0.16,
    blur: 0,
    dx: -10,
    dy: 18,
    r0: 0,
    r1: 20,
    dur: 9,
    delay: -1,
  },
  {
    shape: "diamond",
    left: 19,
    top: 85,
    size: 84,
    opacity: 0.1,
    blur: 1,
    dx: 18,
    dy: -14,
    r0: 10,
    r1: -6,
    dur: 13,
    delay: -6,
  },
  {
    shape: "sparkle",
    left: 7,
    top: 41,
    size: 52,
    opacity: 0.18,
    blur: 0,
    dx: 8,
    dy: 22,
    r0: -6,
    r1: 14,
    dur: 10,
    delay: -3,
  },
  {
    shape: "gem",
    left: 29,
    top: 3,
    size: 74,
    opacity: 0.11,
    blur: 1,
    dx: -14,
    dy: 16,
    r0: 8,
    r1: -8,
    dur: 15,
    delay: -9,
  },
  {
    shape: "sparkle",
    left: 62,
    top: 2,
    size: 56,
    opacity: 0.15,
    blur: 0,
    dx: 10,
    dy: 20,
    r0: 12,
    r1: -4,
    dur: 12,
    delay: -5,
  },
  {
    shape: "club",
    left: 84,
    top: 9,
    size: 130,
    opacity: 0.1,
    blur: 2,
    dx: -12,
    dy: 24,
    r0: 6,
    r1: -10,
    dur: 16,
    delay: -8,
  },
  {
    shape: "heart",
    left: 82,
    top: 29,
    size: 62,
    opacity: 0.16,
    blur: 0,
    dx: 16,
    dy: -18,
    r0: -10,
    r1: 8,
    dur: 10,
    delay: -2,
  },
  {
    shape: "chip",
    left: 89,
    top: 45,
    size: 170,
    opacity: 0.08,
    blur: 4,
    dx: -18,
    dy: -20,
    r0: -4,
    r1: 12,
    dur: 19,
    delay: -11,
  },
  {
    shape: "spade",
    left: 78,
    top: 73,
    size: 100,
    opacity: 0.12,
    blur: 1,
    dx: 14,
    dy: 16,
    r0: 10,
    r1: -6,
    dur: 13,
    delay: -4,
  },
  {
    shape: "star",
    left: 92,
    top: 83,
    size: 76,
    opacity: 0.14,
    blur: 0,
    dx: -12,
    dy: -22,
    r0: 0,
    r1: -18,
    dur: 11,
    delay: -7,
  },
  {
    shape: "gem",
    left: 71,
    top: 91,
    size: 88,
    opacity: 0.1,
    blur: 2,
    dx: 10,
    dy: -18,
    r0: -8,
    r1: 6,
    dur: 17,
    delay: -13,
  },
  {
    shape: "diamond",
    left: 40,
    top: 93,
    size: 64,
    opacity: 0.12,
    blur: 0,
    dx: -16,
    dy: -14,
    r0: 6,
    r1: -12,
    dur: 12,
    delay: -6,
  },
  {
    shape: "club",
    left: 55,
    top: 90,
    size: 92,
    opacity: 0.09,
    blur: 3,
    dx: 12,
    dy: -20,
    r0: -6,
    r1: 10,
    dur: 15,
    delay: -10,
  },
];

function DecoLayer() {
  return (
    <div className="cg-deco" aria-hidden="true">
      {DECO.map((d, i) => (
        <span
          key={i}
          className="cg-deco-item"
          style={
            {
              left: `${d.left}%`,
              top: `${d.top}%`,
              width: d.size,
              height: d.size,
              opacity: d.opacity,
              filter: d.blur ? `blur(${d.blur}px)` : undefined,
              animationDuration: `${d.dur}s`,
              animationDelay: `${d.delay}s`,
              "--dx": `${d.dx}px`,
              "--dy": `${d.dy}px`,
              "--r0": `${d.r0}deg`,
              "--r1": `${d.r1}deg`,
            } as CSSProperties
          }
        >
          <DecoShapeSvg shape={d.shape} id={`cg-deco-grad-${i}`} />
        </span>
      ))}
    </div>
  );
}

/**
 * Melysegi hatter: a legtavolabbi sikon a kabala arca, lilara szinezve es
 * elmosva, elotte a lebego alakzatok (eles sik), legelol pedig fokuszalatlan
 * bokeh-gombok. A harom reteg egyutt adja azt, hogy a kamera egy adott
 * melysegre fokuszal: ami tavolabb vagy kozelebb van, az elmosodik.
 */
function Backdrop() {
  return (
    <div className="cg-backdrop" aria-hidden="true">
      <div className="cg-backdrop-halo" />
      {/* Csak a FEJ kell, nem az egesz kabala: a doboz kivagja a kepbol a fej
          teglalapjat, a maszk pedig lagyan elmossa a vagas szeleit, hogy ne
          egy leragasztott negyzet legyen. */}
      <div className="cg-backdrop-face">
        <img src="/padre.png" alt="" />
      </div>
    </div>
  );
}

type BokehItem = {
  left: number;
  top: number;
  size: number;
  opacity: number;
  dx: number;
  dy: number;
  dur: number;
  delay: number;
};

/**
 * A gombok NEM `filter: blur()`-rel keszulnek, hanem radialis gradienssel.
 * Ugyanaz a lagy folt, csak nincs mogotte raszterizalt blur-reteg, amit egy
 * OBS bongeszo-forrasnak minden kepkockan kezelnie kellene.
 */
const BOKEH: BokehItem[] = [
  {
    left: 4,
    top: 18,
    size: 300,
    opacity: 0.1,
    dx: 18,
    dy: 26,
    dur: 22,
    delay: -3,
  },
  {
    left: 25,
    top: 71,
    size: 190,
    opacity: 0.08,
    dx: -22,
    dy: -18,
    dur: 26,
    delay: -12,
  },
  {
    left: 45,
    top: 5,
    size: 150,
    opacity: 0.07,
    dx: 14,
    dy: 22,
    dur: 20,
    delay: -8,
  },
  {
    left: 66,
    top: 64,
    size: 260,
    opacity: 0.09,
    dx: -16,
    dy: -24,
    dur: 28,
    delay: -17,
  },
  {
    left: 87,
    top: 20,
    size: 340,
    opacity: 0.07,
    dx: -20,
    dy: 20,
    dur: 24,
    delay: -5,
  },
  {
    left: 35,
    top: 38,
    size: 110,
    opacity: 0.11,
    dx: 20,
    dy: -16,
    dur: 18,
    delay: -10,
  },
  {
    left: 74,
    top: 41,
    size: 130,
    opacity: 0.09,
    dx: -18,
    dy: 18,
    dur: 19,
    delay: -2,
  },
  {
    left: 13,
    top: 87,
    size: 220,
    opacity: 0.06,
    dx: 16,
    dy: -20,
    dur: 27,
    delay: -14,
  },
];

function BokehLayer() {
  return (
    <div className="cg-bokeh" aria-hidden="true">
      {BOKEH.map((b, i) => (
        <span
          key={i}
          className="cg-bokeh-item"
          style={
            {
              left: `${b.left}%`,
              top: `${b.top}%`,
              width: b.size,
              height: b.size,
              opacity: b.opacity,
              animationDuration: `${b.dur}s`,
              animationDelay: `${b.delay}s`,
              "--dx": `${b.dx}px`,
              "--dy": `${b.dy}px`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/**
 * Negative animation-delay a fazis indulasa ota eltelt idore.
 *
 * Ezzel a savok es a gyuru CSS animaciokent futnak, a bongeszo sajat
 * utemezesevel, nem React re-renderbol. A memo a fazis indulasara kulcsol, igy
 * a masodpercenkenti ujrarajzolas nem inditja ujra az animaciot.
 */
function useAnimationOffset(startedAt: number) {
  return useMemo(() => Math.min(0, startedAt - Date.now()), [startedAt]);
}

/** Másodperc alapú visszaszámláló, a fázis indulásából számolva. */
function useCountdown(startedAt: number, durationMs: number, active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      setNow(Date.now());
      raf.current = window.setTimeout(tick, 250) as unknown as number;
    };
    tick();
    return () => {
      alive = false;
      if (raf.current) window.clearTimeout(raf.current);
    };
  }, [active, startedAt, durationMs]);

  const remainingMs = Math.max(0, startedAt + durationMs - now);
  return { seconds: Math.ceil(remainingMs / 1000) };
}

/** Ikonok SVG-ben: az emoji fontfuggo, OBS-ben kockakent is megjelenhet. */
function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M18 4h2a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4h-.35A6 6 0 0 1 13 14.9V17h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.1A6 6 0 0 1 7.35 11H7a4 4 0 0 1-4-4V5a1 1 0 0 1 1-1h2V3h12zm0 2v3a2 2 0 0 0 2-2V6zM4 6v1a2 2 0 0 0 2 2V6z" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2 9.6 6.7 4.4 7.5l3.8 3.7-.9 5.2 4.7-2.5 4.7 2.5-.9-5.2 3.8-3.7-5.2-.8zM8 18.4V22l4-1.7 4 1.7v-3.6l-4 2.1z" />
    </svg>
  );
}

function TimerRing({
  seconds,
  durationMs,
  startedAt,
  label = "seconds",
}: {
  seconds: number;
  durationMs: number;
  startedAt: number;
  label?: string;
}) {
  const offsetMs = useAnimationOffset(startedAt);
  const low = seconds <= 5;
  return (
    <div className="cg-timer-wrap">
      <div className={`cg-timer-circle${low ? " cg-timer-pulse" : ""}`}>
        {/* Track and progress are the same circle geometry inside one svg. A CSS
            border plus an svg ring would sit on slightly different radii and
            read as a doubled outline. */}
        <svg viewBox="0 0 62 62">
          <circle className="cg-timer-track" cx="31" cy="31" r="28" />
          <circle
            className="cg-timer-progress"
            cx="31"
            cy="31"
            r="28"
            style={{
              animationDuration: `${durationMs}ms`,
              animationDelay: `${offsetMs}ms`,
            }}
          />
        </svg>
        <span>{seconds}</span>
      </div>
      <span className="cg-timer-text">{label}</span>
    </div>
  );
}

function Card({
  card,
  flipped,
  winner,
  loser,
  flipDelayMs = 0,
}: {
  card: CardConfig;
  flipped: boolean;
  winner?: boolean;
  loser?: boolean;
  /** A kartyak egyesevel fordulnak: minden kovetkezo ennyivel kesobb. */
  flipDelayMs?: number;
}) {
  return (
    <div
      className={[
        "cg-card-wrap",
        winner ? "cg-winner-glow" : "",
        loser ? "cg-loser" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={`cg-card${flipped ? " cg-flipped" : ""}`}
        style={
          flipDelayMs ? { transitionDelay: `${flipDelayMs}ms` } : undefined
        }
      >
        <div className="cg-card-back">
          <img className="cg-logo-img" src="/padre.png" alt="" />
        </div>
        <div className="cg-card-front">
          <div className="cg-prize-amount">{renderPrize(card.prize)}</div>
          {card.prizeNote ? (
            <div className="cg-prize-desc">{card.prizeNote}</div>
          ) : null}
          <div className="cg-winners-badge">
            {card.winnerCount} {card.winnerCount === 1 ? "winner" : "winners"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CardGame({
  state: incoming,
}: {
  state: CardGameState | null;
}) {
  const live = incoming && incoming.phase !== "idle" ? incoming : null;

  // The panel has to stay mounted while it slides away, so the last live state
  // is kept and rendered during the exit. Without it the game would disappear
  // in a single frame when the admin resets.
  const [closing, setClosing] = useState<CardGameState | null>(null);
  const lastLive = useRef<CardGameState | null>(null);
  const exitTimer = useRef<number | null>(null);

  useEffect(() => {
    if (live) {
      lastLive.current = live;
      if (exitTimer.current) {
        window.clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setClosing(null);
      return;
    }
    if (!lastLive.current) return;
    setClosing(lastLive.current);
    lastLive.current = null;
    exitTimer.current = window.setTimeout(() => setClosing(null), EXIT_MS);
  }, [live]);

  useEffect(
    () => () => {
      if (exitTimer.current) window.clearTimeout(exitTimer.current);
    },
    [],
  );

  const state = live ?? closing;
  const phase = state?.phase ?? "idle";

  const { seconds } = useCountdown(
    state?.phaseStartedAt ?? 0,
    state?.phaseDurationMs ?? 0,
    Boolean(live) && (phase === "voting" || phase === "collecting"),
  );

  // A nyertes ragyogasa csak azutan jon fel, hogy az utolso kartya is
  // megfordult. Kulonben a glow mar akkor elarulna a nyertest, amikor a tobbi
  // kartya meg a hatlapjat mutatja.
  const cardCount = state?.cards.length ?? 0;
  const [flipStarted, setFlipStarted] = useState(false);
  const [revealDone, setRevealDone] = useState(false);
  useEffect(() => {
    if (phase !== "revealing") {
      setFlipStarted(false);
      setRevealDone(false);
      return;
    }
    const startFlip = window.setTimeout(
      () => setFlipStarted(true),
      PRE_FLIP_MS,
    );
    const done = window.setTimeout(
      () => setRevealDone(true),
      PRE_FLIP_MS + FLIP_MS + Math.max(0, cardCount - 1) * FLIP_STAGGER_MS,
    );
    return () => {
      window.clearTimeout(startFlip);
      window.clearTimeout(done);
    };
  }, [phase, cardCount]);

  const phaseOffsetMs = useAnimationOffset(state?.phaseStartedAt ?? 0);

  const winnerCard = useMemo(() => {
    if (!state || state.winnerCardIndex === null) return null;
    return state.cards[state.winnerCardIndex] ?? null;
  }, [state]);

  if (!state) return null;

  /** Trimelve, mert az admin mezoje szokozzel is elmehet. */
  const collectKeyword = (state.collectKeyword ?? "").trim();

  const winnerPct =
    state.winnerCardIndex !== null
      ? state.percentages[state.winnerCardIndex]
      : undefined;

  return (
    <div className={`cg-root${live ? "" : " cg-exit"}`}>
      <style>{CSS}</style>

      {/* Melysegi retegek, hatulrol elore. Mind a stage ELOTT all a DOM-ban,
          igy a kartyak mindig felettuk vannak -- nem kell z-index-szel bajlodni.
          Tavoli sik -> eles sik -> kozeli sik -> fokusz-elsotetules. */}
      <Backdrop />
      <DecoLayer />
      <BokehLayer />
      <div className="cg-focus" aria-hidden="true" />

      {/* The stage carries the slide, the root only clips it. Animating the
          root itself would push the panel outside the screen cell and over the
          camera during the first and last frames. */}
      <div className="cg-stage">
        {/* Keyed by phase so the fade replays on every phase change. */}
        <div className="cg-phase" key={phase}>
          {phase === "voting" && (
            <>
              <div className="cg-title">
                {state.voteTitle || DEFAULT_VOTE_TITLE}
              </div>
              <div className="cg-sub">
                Type 1–{state.cards.length} in the chat
              </div>

              <TimerRing
                seconds={seconds}
                durationMs={state.phaseDurationMs}
                startedAt={state.phaseStartedAt}
              />

              <div className="cg-cards-row">
                {state.cards.map((card, index) => (
                  <div className="cg-card-slot" key={card.label}>
                    <div className="cg-card-number">{card.label}</div>
                    <Card card={card} flipped={false} />
                    <div className="cg-card-pct">
                      {Math.round(state.percentages[index] ?? 0)}%
                    </div>
                  </div>
                ))}
              </div>

              {/* A sav CSS animacio, nem React allapot: a szazalek masodpercenkent
              frissul, egy abbol szamolt szelesseg lepcsozne. */}
              <div className="cg-phase-bar">
                <div
                  className="cg-phase-bar-fill"
                  style={{
                    animationDuration: `${state.phaseDurationMs}ms`,
                    animationDelay: `${phaseOffsetMs}ms`,
                  }}
                />
              </div>
            </>
          )}

          {phase === "revealing" && (
            <>
              <div className="cg-title" style={{ marginBottom: 40 }}>
                Results
              </div>

              <div className="cg-cards-row">
                {state.cards.map((card, index) => (
                  <div className="cg-card-slot" key={card.label}>
                    <div className="cg-card-number">{card.label}</div>
                    <Card
                      card={card}
                      flipped={flipStarted}
                      flipDelayMs={index * FLIP_STAGGER_MS}
                      winner={revealDone && index === state.winnerCardIndex}
                      loser={
                        revealDone &&
                        state.winnerCardIndex !== null &&
                        index !== state.winnerCardIndex
                      }
                    />
                    <div
                      className={`cg-card-pct${index === state.winnerCardIndex ? " cg-winner-pct" : ""}`}
                    >
                      {Math.round(state.percentages[index] ?? 0)}%
                    </div>
                  </div>
                ))}
              </div>

              {/* A szoveg is megvarja az utolso kartyat, kulonben elarulja a
              nyertest, mielott a nezo latna a lapokat. */}
              {/* Az ikon es a szoveg egy sorban: a sor flex, es a TELJES mondat
              EGY gyerek benne. Ha a szovegcsomok kulon flex elemek lennenek, a
              gap minden szodarab koze beallna. */}
              {winnerCard && revealDone ? (
                <div className="cg-reveal-note">
                  <TrophyIcon className="cg-inline-icon" />
                  <span>
                    The winner is{" "}
                    <span style={{ color: NEON_SOFT }}>
                      card {winnerCard.label}
                    </span>
                    {typeof winnerPct === "number"
                      ? ` with ${winnerPct.toFixed(1)}% of the votes`
                      : ""}
                  </span>
                </div>
              ) : null}
            </>
          )}

          {phase === "collecting" && winnerCard && (
            <div className="cg-side-layout">
              <div className="cg-card-slot">
                <Card card={winnerCard} flipped winner />
              </div>

              <div className="cg-side-right">
                <div>
                  {/* Nincs kezi sortores: a cim mostmar az adminrol jon, es egy
                      beegetett <br /> egy masik hosszusagu szoveget elrontana.
                      A tordelest a doboz szelessegere bizzuk. */}
                  <div className="cg-side-label">
                    {state.collectTitle || collectTitleFor(collectKeyword)}
                  </div>
                  <div className="cg-side-sub">
                    {renderPrize(state.collectSubtitle || collectSubtitleFor(collectKeyword))}
                  </div>
                </div>

                <TimerRing
                  seconds={seconds}
                  durationMs={state.phaseDurationMs}
                  startedAt={state.phaseStartedAt}
                />

                <div className="cg-sponsor">
                  <BadgeIcon className="cg-sponsor-icon" />
                  <span>
                    {state.sponsorText ||
                      "Register on neverdoit.com to participate"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {phase === "done" && winnerCard && (
            <div className="cg-side-layout">
              <div className="cg-card-slot">
                <Card card={winnerCard} flipped winner />
              </div>

              <div className="cg-winners-panel">
                <h3>Winners</h3>
                {state.winners.length === 0 ? (
                  <div className="cg-winner-row cg-second">
                    <span>No entries</span>
                  </div>
                ) : (
                  state.winners.map((name) => (
                    <div key={name} className="cg-winner-row cg-first">
                      <TrophyIcon className="cg-row-icon" />
                      <span>{name}</span>
                      <span className="cg-prize-tag">{renderPrize(winnerCard.prize)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.cg-root {
  position: absolute;
  inset: 0;
  z-index: 20;
  border-radius: 20px;
  background: ${hexToRgba(BG, BG_OPACITY)};
  overflow: hidden;
  font-family: 'Segoe UI', system-ui, sans-serif;
  color: #fff;
  animation: cg-fade-in .5s ease-out both;
}
.cg-root.cg-exit { animation: cg-fade-out .8s ease-in both; }

/* The stage slides, the root clips it, so nothing ever leaves the screen cell. */
.cg-stage {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: cg-slide-up 1s cubic-bezier(.16,.84,.24,1) both;
}
.cg-exit .cg-stage { animation: cg-slide-down .8s cubic-bezier(.6,.04,.98,.34) both; }

.cg-phase {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: cg-phase-in 1.1s cubic-bezier(.16,.84,.24,1) both;
}

@keyframes cg-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes cg-fade-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes cg-slide-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@keyframes cg-slide-down {
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
}
@keyframes cg-phase-in {
  from { opacity: 0; transform: translateY(46px) scale(.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.cg-root::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, ${NEON}22 0%, transparent 70%);
  pointer-events: none;
}

/* --- Melysegi hatter -------------------------------------------------- */
.cg-backdrop {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: 20px;
}
/* Lagy lila derengés a fej mogott, hogy ne egy lebego folt legyen a semmiben. */
.cg-backdrop-halo {
  position: absolute;
  left: 50%; top: 50%;
  width: 900px; height: 700px;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse at 50% 50%,
    ${hexToRgba(NEON, 0.14)} 0%,
    ${hexToRgba(NEON_DEEP, 0.07)} 44%,
    transparent 74%);
}
/*
 * A lila szinezes filter-lanccal keszul, nem mix-blend-mode-dal: a blend
 * modok a szulo opacity-je altal letrehozott csoportban bongeszonkent maskepp
 * viselkednek, a filter viszont mindenhol ugyanaz. grayscale -> sepia ad egy
 * ismert (barnas) alaphangot, azt forgatjuk at lilaba.
 */
/*
 * A vagas szamokkal: a fej a 1024x1024-es kepen kb. x 200..800, y 80..620.
 * A dobozt erre a kivagasra meretezzuk, a kepet pedig annyira nagyitjuk, hogy
 * a fej pont kitoltse. Ha a padre.png kicserelodik, ez a negy szam allitando.
 */
.cg-backdrop-face {
  position: absolute;
  /*
   * Vizszintes finomhangolas EGY szammal: pozitiv ertek jobbra tolja a fejet,
   * negativ balra.
   *
   * Mertani kozepen a fej NEM latszik kozepen, es ez nem a kod hibaja: a rajz
   * maga aszimmetrikus. A nyitott szem es a ket vilagos jatszokartya balra
   * esik, jobbra csak a sotet kalapkarima jut, es egy halvany vizjelnel a szem
   * a vilagos foltot keresi, nem a mertani kozepet. Az eltolas ezt egyenliti ki.
   */
  /*
   * 95px MERT ertek: az eles oldal kepernyokepen a fej lathato tomegenek
   * sulypontja 55px-nel meg 39px-re allt a panel kozeptol balra.
   * (A 255px-nel a fej a nevgyujto kepernyon rarakodik a szovegre.)
   */
  --face-nudge-x: 95px;
  left: 50%; top: 44%;
  width: 640px; height: 580px;
  transform: translate(calc(-50% + var(--face-nudge-x)), -50%);
  overflow: hidden;
  opacity: .2;
  filter: grayscale(1) sepia(1) hue-rotate(238deg) saturate(3.4) brightness(1.25) blur(2.5px);
  /*
   * A maszk kozeppontja szandekosan NEM a doboz kozepe: a kivagas bal felso
   * sarkaban vannak a vilagos jatszokartyak, es egy halvany vizjelnel a
   * legvilagosabb folt huzza a szemet. Jobbra tolt maszkkal a kartyak
   * beleolvadnak, es az ARC kerul a panel kozepere.
   */
  -webkit-mask-image: radial-gradient(ellipse 50% 54% at 53% 48%, #000 0%, #000 36%, transparent 78%);
  mask-image: radial-gradient(ellipse 50% 54% at 53% 48%, #000 0%, #000 36%, transparent 78%);
  animation: cg-backdrop-breathe 19s ease-in-out infinite alternate;
}
.cg-backdrop-face img {
  position: absolute;
  width: 1092px; height: 1092px;
  left: -213px; top: -85px;
  display: block;
}
@keyframes cg-backdrop-breathe {
  from { transform: translate(calc(-50% + var(--face-nudge-x)), -50%) scale(1); }
  to   { transform: translate(calc(-50% + var(--face-nudge-x)), -50%) scale(1.06); }
}

/* --- Kozeli, fokuszalatlan sik ---------------------------------------- */
.cg-bokeh {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: 20px;
}
.cg-bokeh-item {
  position: absolute;
  display: block;
  border-radius: 50%;
  transform-origin: 50% 50%;
  will-change: transform;
  background: radial-gradient(circle at 38% 34%,
    ${hexToRgba(NEON_SOFT, 0.55)} 0%,
    ${hexToRgba(NEON, 0.3)} 44%,
    transparent 70%);
  animation-name: cg-bokeh-drift;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-fill-mode: both;
}
@keyframes cg-bokeh-drift {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(var(--dx), var(--dy), 0); }
}

/*
 * Fokusz-elsotetules: a panel szele a hatter szinebe megy at, a kozepe
 * erintetlen marad. Ez a klasszikus "ide nezz" jelzes, es egyben elrejti,
 * hogy a hatteralakzatok a szeleken elvagodnak.
 */
.cg-focus {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: 20px;
  background: radial-gradient(ellipse 64% 60% at 50% 50%,
    transparent 0%,
    transparent 52%,
    ${hexToRgba(BG, 0.24)} 80%,
    ${hexToRgba(BG, 0.58)} 100%);
}

/* Lebego hatter-alakzatok. Csak transform animalodik, layoutot nem mozgat. */
.cg-deco {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: 20px;
}
.cg-deco-item {
  position: absolute;
  display: block;
  transform-origin: 50% 50%;
  will-change: transform;
  animation-name: cg-deco-float;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-fill-mode: both;
}
.cg-deco-item svg { display: block; width: 100%; height: 100%; }
@keyframes cg-deco-float {
  from { transform: translate3d(0, 0, 0) rotate(var(--r0)); }
  to   { transform: translate3d(var(--dx), var(--dy), 0) rotate(var(--r1)); }
}
.cg-title {
  font-size: 48px;
  font-weight: 900;
  letter-spacing: 3px;
  text-transform: uppercase;
  text-shadow: 0 0 40px ${NEON}55;
  margin-bottom: 6px;
}
.cg-sub {
  font-size: 20px;
  font-weight: 600;
  /* Vilagosabb mint a MUTED szurke: a hatter-arc folott a #b1b1b1 beleolvadt. */
  color: #d3c8ee;
  margin-bottom: 34px;
  letter-spacing: 1px;
  text-shadow: 0 2px 12px ${hexToRgba(BG, 0.9)};
}
/*
 * A visszaszamlalo sajat, enyhen sotet tablan ul. A hatter-arc pont mogotte
 * van, es nelkule a szam es a felirat beleolvadt -- ez a legfontosabb
 * informacio a kepernyon, nem versenyezhet a dekoracioval.
 */
.cg-timer-wrap {
  display: flex; align-items: center; gap: 16px; margin-bottom: 34px;
  padding: 10px 26px 10px 12px;
  border-radius: 999px;
  background: ${hexToRgba(BG, 0.62)};
  border: 1px solid ${hexToRgba(NEON, 0.28)};
  box-shadow: 0 0 30px ${hexToRgba(BG, 0.8)};
}
.cg-timer-circle {
  width: 84px; height: 84px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; font-weight: 800; color: #fff;
  text-shadow: 0 0 18px ${hexToRgba(NEON, 0.9)};
  position: relative;
}
.cg-timer-circle svg {
  position: absolute; top: 0; left: 0;
  width: 84px; height: 84px; transform: rotate(-90deg);
}
.cg-timer-circle svg circle {
  fill: none; stroke-width: 3; stroke-linecap: round;
}
.cg-timer-track { stroke: ${NEON}; opacity: .32; }
/* A gyuru CSS animacio, a fazis teljes hosszaban, linearisan. React
   allapotbol lepteve masodpercenkent ugrana egyet. */
.cg-timer-progress {
  stroke: ${NEON};
  stroke-dasharray: 176;
  animation-name: cg-ring-drain;
  animation-timing-function: linear;
  animation-fill-mode: both;
}
@keyframes cg-ring-drain {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: 176; }
}
.cg-timer-text {
  font-size: 18px; font-weight: 700; color: #e4dbff;
  letter-spacing: 1px; text-transform: uppercase;
}
@keyframes cg-timer-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.12); } }
.cg-timer-pulse { animation: cg-timer-pulse .5s ease-in-out infinite; color: #ff6b4a; }
.cg-timer-pulse .cg-timer-progress { stroke: #ff6b4a; }
.cg-timer-pulse .cg-timer-track { stroke: #ff6b4a; }

.cg-cards-row { display: flex; gap: 22px; align-items: flex-start; justify-content: center; }
.cg-card-slot { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.cg-card-number { font-size: 20px; font-weight: 700; color: ${MUTED}; }

.cg-card-wrap {
  width: 160px; height: 220px;
  perspective: 1000px;
  transition: filter .4s, opacity .4s, transform .4s;
}
.cg-card {
  width: 100%; height: 100%; border-radius: 16px;
  position: relative; transform-style: preserve-3d;
  transition: transform .8s cubic-bezier(.4,0,.2,1);
}
.cg-card-back, .cg-card-front {
  position: absolute; inset: 0; border-radius: 16px; backface-visibility: hidden;
}
.cg-card-back {
  background: linear-gradient(145deg, ${NEON} 0%, ${NEON_DEEP} 45%, #7a00d6 70%, #3a0080 100%);
  border: 3px solid ${NEON_SOFT};
  box-shadow: 0 8px 32px #0007, inset 0 1px 0 ${NEON_SOFT}33;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
}
.cg-card-back::before {
  content: ''; position: absolute; inset: 8px;
  border: 1.5px solid #ffffff33; border-radius: 10px;
}
.cg-logo-img {
  width: 74%; height: auto; object-fit: contain;
  filter: drop-shadow(0 3px 8px #0006);
  pointer-events: none; user-select: none;
}
/* flex: none, kulonben a flex sor osszenyomja az svg-t es toresnek latszik.
   A tavolsagot a sor gap-je adja, nem margin. */
.cg-inline-icon { width: 22px; height: 22px; flex: none; color: ${NEON_SOFT}; }
.cg-row-icon { width: 18px; height: 18px; flex: none; }
.cg-card-front {
  background: linear-gradient(145deg, ${PANEL} 0%, #150a2c 100%);
  border: 3px solid ${NEON};
  box-shadow: 0 8px 32px #0007;
  transform: rotateY(180deg);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 20px 14px; text-align: center; gap: 8px;
}
.cg-prize-amount { font-size: 28px; font-weight: 900; color: #fff; line-height: 1.1; }
.cg-prize-espont-icon {
  width: .82em; height: .82em; display: inline-block; vertical-align: -0.08em;
  margin: 0 2px; object-fit: contain;
}
.cg-prize-desc { font-size: 13px; font-weight: 600; color: ${MUTED}; line-height: 1.3; }
.cg-winners-badge {
  margin-top: 8px; background: ${NEON}; border-radius: 20px; padding: 4px 12px;
  font-size: 11px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 1px;
}
.cg-flipped { transform: rotateY(180deg); }
.cg-winner-glow {
  filter: drop-shadow(0 0 20px ${NEON}aa) drop-shadow(0 0 6px ${NEON_SOFT});
  z-index: 10; transform: scale(1.06);
}
.cg-winner-glow .cg-card-front { border-color: ${NEON_SOFT}; border-width: 4px; box-shadow: inset 0 0 20px ${NEON}22; }
.cg-loser { opacity: .5; filter: brightness(.6); }

.cg-card-pct { font-size: 18px; font-weight: 700; color: #fff; min-height: 24px; transition: all .4s; }
.cg-winner-pct { color: ${NEON_SOFT}; font-size: 20px; }

.cg-phase-bar {
  width: 600px; height: 7px; background: #2a1550; border-radius: 4px;
  margin-top: 36px; overflow: hidden;
  border: 1px solid ${hexToRgba(NEON, 0.25)};
  box-shadow: 0 0 24px ${hexToRgba(BG, 0.85)};
}
/* Teljes szelessegu sav, amit a transzformacio huz ossze. A width animalasa
   layoutot szamol ujra minden kepkockan, a scaleX a compositoron fut, ezert
   marad folyamatos akkor is, ha a fo szal epp chat uzenetet dolgoz fel. */
.cg-phase-bar-fill {
  width: 100%; height: 100%;
  background: linear-gradient(90deg, ${NEON_DEEP}, ${NEON_SOFT});
  border-radius: 4px;
  box-shadow: 0 0 14px ${hexToRgba(NEON, 0.75)};
  transform-origin: left center;
  animation-name: cg-bar-drain;
  animation-timing-function: linear;
  animation-fill-mode: both;
}
@keyframes cg-bar-drain {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}
/* Egy sor: az ikon es a mondat egymas mellett marad, a szoveg nem tordelodik
   az ikon ala. A .cg-phase flex oszlop kozepre igazit, tehat a sor sajat
   szelessegre zsugorodik es magatol kozepen all. */
.cg-reveal-note {
  margin-top: 32px; font-size: 18px; color: ${MUTED}; font-weight: 600;
  display: flex; align-items: center; gap: 10px; white-space: nowrap;
}

/*
 * A ket oldal kozotti tavolsag es a jobb oszlop belso ritmusa. Az eredeti
 * ertekek egy kisebb kartyahoz keszultek: a 22 px-es focim kisebb volt, mint a
 * timer szama mellette, es a 20 px-es koz miatt a negy elem egy blokknak
 * latszott. A cim most vezet, alatta harom, egyre halkabb szint kovetkezik.
 */
.cg-side-layout { display: flex; gap: 84px; align-items: center; }
.cg-side-right {
  display: flex; flex-direction: column; gap: 30px;
  min-width: 320px; max-width: 380px;
}
.cg-side-label {
  font-size: 34px; font-weight: 800; color: #fff; line-height: 1.15;
  letter-spacing: .5px; text-shadow: 0 0 34px ${hexToRgba(NEON, 0.45)};
}
.cg-side-sub { font-size: 17px; color: #cbbfe6; margin-top: 12px; font-weight: 600; }
.cg-sponsor {
  background: ${NEON}; color: #fff; border-radius: 14px; padding: 13px 18px;
  font-size: 15px; font-weight: 700; display: flex; align-items: center; gap: 10px;
  max-width: 380px; line-height: 1.35;
  box-shadow: 0 10px 30px ${hexToRgba(NEON_DEEP, 0.45)};
}
/* Size in pixels, not font-size: the icon is an svg without intrinsic size, so
   a bare font-size leaves it filling the whole badge. */
.cg-sponsor-icon { width: 22px; height: 22px; flex: none; }

.cg-winners-panel {
  background: #1b0f33; border: 1.5px solid #3a1f6b; border-radius: 16px;
  padding: 20px 24px; min-width: 260px;
}
.cg-winners-panel h3 {
  font-size: 16px; font-weight: 700; color: ${MUTED};
  text-transform: uppercase; letter-spacing: 2px; margin-bottom: 14px;
}
.cg-winner-row {
  display: flex; align-items: center; gap: 10px; padding: 8px 12px;
  border-radius: 10px; margin-bottom: 8px; font-weight: 700; font-size: 15px;
}
.cg-first { background: ${NEON}22; border: 1.5px solid ${NEON}77; color: ${NEON_SOFT}; }
.cg-second { background: #ffffff0d; border: 1.5px solid #ffffff15; color: #d6d0e6; }
.cg-prize-tag { margin-left: auto; font-size: 13px; color: ${MUTED}; font-weight: 600; }
.cg-first .cg-prize-tag { color: ${NEON}; }
`;
